import { buildRunSteps } from './flowUtils';
import type { PipelineNode, PipelineEdge, GroupNode, StickyNote, Scheduler } from '@/mocks/pipeline';
import type { Outcome, PipelineStep } from '@/api';
import { applySavedPositions, coord, layoutCanvasNodes, layoutCanvasSchedulers, type CanvasLayout } from './canvasLayout';

export function hydrateCanvasFromOutcome(
  o: Pick<Outcome, 'nodes' | 'edges' | 'groups' | 'stickies' | 'schedulers'>,
  skillMap: Record<string, { body?: string }>,
  savedLayout?: CanvasLayout | null,
): { nodes: PipelineNode[]; edges: PipelineEdge[]; groups: GroupNode[]; stickies: StickyNote[]; schedulers: Scheduler[] } {
  const rawNodes: PipelineNode[] = (o.nodes || []).map((n) => ({
    id: n.id,
    skillId: n.skill_id,
    x: coord(n.x),
    y: coord(n.y),
    status: 'idle',
    note: n.note ?? '',
    body: n.body ?? skillMap[n.skill_id]?.body ?? '',
  }));
  const nodes = layoutCanvasNodes(applySavedPositions(rawNodes, savedLayout?.nodes));
  const groups: GroupNode[] = applySavedPositions(
    (o.groups || []).map((g) => ({
      id: g.id,
      type: g.type as GroupNode['type'],
      x: coord(g.x),
      y: coord(g.y),
      w: g.w || 340,
      h: g.h || 220,
      title: g.title,
      status: 'idle' as const,
      iterations: g.iterations || 1,
    })),
    savedLayout?.groups,
  );
  const stickies: StickyNote[] = applySavedPositions(
    (o.stickies || []).map((s) => ({
      id: s.id,
      x: coord(s.x),
      y: coord(s.y),
      w: s.w,
      h: s.h,
      text: s.text,
    })),
    savedLayout?.stickies,
  );
  const edges: PipelineEdge[] = (o.edges || []).map((e) => ({ id: e.id, from: e.from, to: e.to, field: e.field }));
  const schedulers: Scheduler[] = layoutCanvasSchedulers(applySavedPositions(
    (o.schedulers || []).map((s) => ({
      id: s.id,
      x: coord(s.x),
      y: coord(s.y),
      title: s.title || 'Scheduler',
      enabled: !!s.enabled,
      cadence: s.cadence || '@every 6h',
      timezone: s.timezone,
      action: s.action,
      targetIds: s.target_ids,
      lastFiredAt: s.last_fired_at,
    })),
    savedLayout?.schedulers,
  ));
  return { nodes, edges, groups, stickies, schedulers };
}

// buildOutcomePayload maps the live canvas into the backend Outcome shape.
export function buildOutcomePayload(
  pipelineName: string,
  description: string,
  nodes: PipelineNode[],
  edges: PipelineEdge[],
  groups: GroupNode[],
  stickies: StickyNote[],
  schedulers: Scheduler[] = [],
): Partial<Outcome> {
  return {
    title: pipelineName,
    description,
    nodes: nodes.map((n) => ({ id: n.id, skill_id: n.skillId, x: Math.round(coord(n.x)), y: Math.round(coord(n.y)), note: n.note, body: n.body })),
    edges: edges.map((e) => ({ id: e.id, from: e.from, to: e.to, field: e.field })),
    groups: groups.map((g) => ({ id: g.id, type: g.type, x: Math.round(coord(g.x)), y: Math.round(coord(g.y)), w: g.w, h: g.h, title: g.title, iterations: g.iterations })),
    stickies: stickies.map((s) => ({ id: s.id, x: Math.round(coord(s.x)), y: Math.round(coord(s.y)), w: s.w, h: s.h, text: s.text })),
    schedulers: schedulers.map((s) => ({
      id: s.id,
      x: Math.round(coord(s.x)),
      y: Math.round(coord(s.y)),
      title: s.title,
      enabled: s.enabled,
      cadence: s.cadence,
      timezone: s.timezone,
      action: s.action,
      target_ids: s.targetIds,
      last_fired_at: s.lastFiredAt,
    })),
    pipeline_steps: buildRunSteps(nodes, groups).map((id, i) => {
      const n = nodes.find((x) => x.id === id);
      const incoming = edges.filter((e) => e.to === id);
      const inputs = incoming.length ? incoming.map((e) => ({ source: e.from, field: e.field })) : [{ source: 'trigger', field: 'text' }];
      return { id: `s${i + 1}`, skill_id: n?.skillId || '', inputs } as PipelineStep;
    }),
  };
}

export function applyLiveRunToNodes(
  nodes: PipelineNode[],
  currentSkillId: string | undefined,
  steps: { skill_id: string; status: string }[] | undefined,
): PipelineNode[] {
  const done = new Map((steps || []).map((s) => [s.skill_id, s.status]));
  return nodes.map((n) => {
    const st = done.get(n.skillId);
    if (st === 'success') return { ...n, status: 'success' as const };
    if (st === 'failure' || st === 'failed') return { ...n, status: 'error' as const };
    if (currentSkillId && n.skillId === currentSkillId) return { ...n, status: 'running' as const };
    return n;
  });
}

export function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}
